import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import Status from "components/Shared/Status";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useAuth } from "hooks";
import { localizeDateTimeShort } from "utils/dateFormats";
import { formatTimeRemaining } from "utils/holdDuration";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import FileIcon from "components/Shared/FileIcon";

const statusStyles = {
    Pending: { backgroundColor: "#F8C471", textColor: "#D35400" },
    Delivered: { backgroundColor: "#DFF7DF", textColor: "green" },
    Return: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
    Cancelled: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
    Collected: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    Loaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    Unloaded: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    CrossDocked: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    SendOut: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    Warehousing: { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    DeliveryFailed: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
    default: { backgroundColor: "#808080", textColor: "#FFFFFF" },
};
const getStatusStyle = (status) => statusStyles[status] || statusStyles.default;

const Row = ({ label, children, value, fullSpan = false }) => (
    <Box sx={{
        display: "flex", py: 0.6, px: 1.5, gap: 1, borderRadius: 1,
        transition: "background-color 0.15s ease",
        "&:hover": { bgcolor: "action.hover" },
        ...(fullSpan ? { gridColumn: "1 / -1" } : {}),
    }}>
        <Box sx={{ width: "45%", color: "text.primary", fontSize: "0.66rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, flexShrink: 0 }}>
            {label}
        </Box>
        <Box sx={{ width: "55%", fontSize: "0.78rem", fontWeight: 600, wordBreak: "break-word" }}>
            {children ? children : value ?? "N/A"}
        </Box>
    </Box>
);

const ShipmentBasicDetailCompact = () => {
    const { isCustomer, isVendor, isContractor, isDriver, isVendorStaff } = useAuth();
    const canGoToLink = isCustomer || isVendor || isContractor || isDriver || isVendorStaff;
    const { ShipmentDetail, loadingShipment } = useShipmentDetailContext();

    // Refresh the "time remaining" label every 30s while on hold.
    const [, setTick] = React.useState(0);
    React.useEffect(() => {
        if (!ShipmentDetail?.isOnHold) return;
        const id = setInterval(() => setTick((t) => t + 1), 30000);
        return () => clearInterval(id);
    }, [ShipmentDetail?.isOnHold]);

    if (loadingShipment) return <InfoGridSkeleton />;

    return (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <Row label="Shipment ID" value={ShipmentDetail?.shipmentId?.replace(/(.{4})/g, "$1-").slice(0, -1)} />
            <Row label="Created" value={ShipmentDetail?.createdAt ? localizeDateTimeShort(ShipmentDetail?.createdAt) : "N/A"} />
            <Row label={ShipmentDetail?.vendor ? "Vendor Profile" : "Customer Profile"}>
                {ShipmentDetail?.vendor && typeof ShipmentDetail.vendor === "object" ? (
                    canGoToLink ? ShipmentDetail.vendor.shopName || "N/A" : (
                        <OpenInNewTab label={ShipmentDetail.vendor.shopName || "N/A"} link={`/app/crm/vendor-profile/${ShipmentDetail.vendor._id}/detail`} />
                    )
                ) : ShipmentDetail?.customerId && typeof ShipmentDetail.customerId === "object" ? (
                    canGoToLink ? ShipmentDetail.customerId.name || ShipmentDetail.customerId.customerUser?.name || "N/A" : (
                        <OpenInNewTab label={ShipmentDetail.customerId.name || ShipmentDetail.customerId.customerUser?.name || "N/A"} link={`/app/crm/customerProfile/${ShipmentDetail.customerId._id}/detail`} />
                    )
                ) : "N/A"}
            </Row>
            <Row label="Receiver">
                {ShipmentDetail?.receiver && typeof ShipmentDetail.receiver === "object" ? (
                    canGoToLink ? ShipmentDetail.receiver.name || ShipmentDetail.receiver.customerUser?.name || "N/A" : (
                        <OpenInNewTab label={ShipmentDetail.receiver.name || ShipmentDetail.receiver.customerUser?.name || "N/A"} link={`/app/crm/customerProfile/${ShipmentDetail.receiver._id}/detail`} />
                    )
                ) : "N/A"}
            </Row>
            <Row label="Source Branch">
                {ShipmentDetail?.sourceBranch && typeof ShipmentDetail.sourceBranch === "object" ? (
                    canGoToLink ? ShipmentDetail.sourceBranch.name || "N/A" : (
                        <OpenInNewTab label={ShipmentDetail.sourceBranch.name || "N/A"} link={`/app/crm/branch/${ShipmentDetail.sourceBranch._id}/detail`} />
                    )
                ) : "N/A"}
            </Row>
            <Row label="Destination Branch">
                {ShipmentDetail?.destinationBranch && typeof ShipmentDetail.destinationBranch === "object" ? (
                    canGoToLink ? ShipmentDetail.destinationBranch.name || "N/A" : (
                        <OpenInNewTab label={ShipmentDetail.destinationBranch.name || "N/A"} link={`/app/crm/branch/${ShipmentDetail.destinationBranch._id}/detail`} />
                    )
                ) : "N/A"}
            </Row>
            <Row label="Receiver Address" value={ShipmentDetail?.receiver?.address} />
            <Row label="Receiver Phone" value={ShipmentDetail?.receiver?.phone} />
            <Row label="Reference" value={ShipmentDetail?.reference} />
            <Row label="Priority" value={ShipmentDetail?.priority?.name} />
            <Row label="Shipment Delivery" value={ShipmentDetail?.shipmentDelivery} />
            <Row label="Delivery Type" value={ShipmentDetail?.deliveryTypes} />
            {ShipmentDetail?.uploadedId?.length > 0 && (
                <Row label="ID Proof">
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {ShipmentDetail.uploadedId.map((file) => (
                            <a key={file?._id} href={file?.url} target="_blank" rel="noreferrer" style={{ color: "var(--primary)" }}>
                                <FileIcon fileName={file?.name} fileUrl={file?.url} />
                            </a>
                        ))}
                    </Box>
                </Row>
            )}
            {ShipmentDetail?.weight && <Row label="Shipment Weight" value={`${ShipmentDetail.weight} kg`} />}
            {ShipmentDetail?.unit?.value && ShipmentDetail?.unit?.unitType?.name && (
                <Row
                    label={ShipmentDetail.unit.unitType.name.toLowerCase() === "weight" ? "Shipment Weight" : "Shipment Unit"}
                    value={
                        ShipmentDetail.unit.unitType.name.toLowerCase() === "weight"
                            ? `${ShipmentDetail.unit.value} kg`
                            : `${ShipmentDetail.unit.value} ${ShipmentDetail.unit.unitType.name}`
                    }
                />
            )}
            {ShipmentDetail?.dimensions?.length > 0 && (
                <Row label="Dimensions" value={`L: ${ShipmentDetail?.dimensions?.length || 0}m, W: ${ShipmentDetail?.dimensions?.width || 0}m, H: ${ShipmentDetail?.dimensions?.height || 0}m`} />
            )}
            <Row label="Pickup Time" value={ShipmentDetail?.shipmentPickupTime ? localizeDateTimeShort(ShipmentDetail?.shipmentPickupTime) : "N/A"} />
            {ShipmentDetail?.shipmentDeliveryTime && (
                <Row
                    label="Delivery Time"
                    value={new Date(ShipmentDetail.shipmentDeliveryTime).toLocaleString("en-US", {
                        year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: true,
                    })}
                />
            )}
            <Row label="In Warehouse?" value={ShipmentDetail?.atWarehouse ? "YES" : "NO"} />
            <Row label="Status"><Status isActive={ShipmentDetail?.isActive} /></Row>
            {ShipmentDetail?.status !== "Pending" && (
                <Row label="Consolidation">
                    <Typography sx={{ fontSize: "0.76rem", fontWeight: 600 }} color={ShipmentDetail?.isConsolidated ? "green" : "orange"}>
                        {ShipmentDetail?.isConsolidated ? "Consolidated" : "Deconsolidated"}
                    </Typography>
                </Row>
            )}
            <Row label="Hold Status">
                <Typography sx={{ fontSize: "0.76rem", fontWeight: 600 }} color={ShipmentDetail?.isOnHold ? "orange" : "green"}>
                    {ShipmentDetail?.isOnHold ? "On Hold" : "Not On Hold"}
                </Typography>
                {ShipmentDetail?.isOnHold && (
                    <Typography sx={{ fontSize: "0.66rem", color: "text.secondary", display: "block", mt: 0.4, lineHeight: 1.4 }}>
                        {ShipmentDetail?.holdStartedAt && (<>Started: {localizeDateTimeShort(ShipmentDetail.holdStartedAt)}<br /></>)}
                        {ShipmentDetail?.holdUntil && (<>Auto-release: {localizeDateTimeShort(ShipmentDetail.holdUntil)}<br /></>)}
                        Remaining: <strong>{formatTimeRemaining(ShipmentDetail?.holdUntil)}</strong>
                    </Typography>
                )}
            </Row>
            {ShipmentDetail?.IsReturning && (
                <Row label="Returning"><Typography sx={{ fontSize: "0.76rem", fontWeight: 600 }} color="red">Returning</Typography></Row>
            )}
            <Row label="Delivery Security">
                {ShipmentDetail?.deliverySecurity ? <Chip label={ShipmentDetail.deliverySecurity} size="small" sx={{ height: 18, fontSize: "0.6rem" }} /> : "N/A"}
            </Row>
            <Row label="Shipment Status">
                <Chip
                    label={ShipmentDetail?.status || "Unknown"}
                    size="small"
                    sx={{ backgroundColor: getStatusStyle(ShipmentDetail?.status).backgroundColor, color: getStatusStyle(ShipmentDetail?.status).textColor, height: 18, fontSize: "0.6rem", fontWeight: "bold" }}
                />
            </Row>
            <Row label="Product Description" value={ShipmentDetail?.productDescription} fullSpan />
            <Row label="Remarks" value={ShipmentDetail?.remarks} fullSpan />
            <Row label="Instruction" value={ShipmentDetail?.instruction} fullSpan />
        </Box>
    );
};

export default ShipmentBasicDetailCompact;